import * as React from 'react';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import ThemeProvider from 'threads5/themes/theme-provider';

import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import type { HeroElement } from 'src/contexts/FirstHeroContext/FirstHeroContext';
import { Wrapper } from 'src/containers';
import { Column, Stack } from 'src/components-v2/Layout';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { CmsMediaSwitch } from 'src/components-v2/Media/CmsMediaSwitch';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import {
  buttonGroupStyles,
  embeddedButtonStyles,
} from 'src/sections/Heroes/CmsHeroSwitch/button-group.styles';

/**
 * @deprecated The Large-Asset-Top-Centered variant does not exist in Figma
 * (SITE-6018); its Storybook story was removed, and the CMS warns editors,
 * hides the appearance for new entries, and disallows cloning entries that
 * use it (SITE-6223; publishing is NOT blocked). ~40 published hero2
 * entries still use it, so this must keep rendering (along with the
 * 'Large-Asset-Top-Centered' case in CmsHeroSwitch) until those entries are
 * migrated. Do not use for new work: the sanctioned replacement is the
 * 'Large-Asset-Centered' appearance (Centered Large Image) with
 * imageOrientation set to 'Top' (SITE-6284).
 */
const CmsLargeAssetTopCenteredHero = ({
  fields,
  sys,
  component = 'header',
}: {
  fields;
  sys;
  component?: HeroElement;
}) => {
  const cmsTheme = useCmsTheme();
  const bgColor = fields?.backgroundColor?.toLowerCase() || 'transparent';
  const mode = useCmsMode({
    mode: fields?.mode,
    backgroundColor: bgColor,
  });
  const componentTheme = useComponentTheme(mode);
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const { asset } = fields || {};
  const background = componentTheme.palette[fields?.backgroundColor];
  const fieldId = 'LargeImageBody';

  return (
    <ThemeProvider theme={componentTheme}>
      <Wrapper
        component={component}
        sx={{ background: background, ...fields?.styles }}
        {...inspectorProps({ fieldId: 'internalTitle' })}
      >
        <Column xs={24} sm={24} lg={20} lgOffset={2} className='image-column'>
          {asset && (
            <CmsMediaSwitch
              asset={{
                ...asset.fields,
                isLazy: false,
                styles: {
                  height: 'auto',
                  width: '100%',
                  ...asset.fields?.styles,
                },
              }}
              queryString={{
                xs: '?fm=webp&w=1226&q=60',
                sm: '?fm=webp&w=1884&q=60',
                md: '?fm=webp&w=2398&q=60',
                lg: '?fm=webp&w=2742&q=60',
              }}
              width={1371} // setting a default width in case it was not provided in CMS
              height={572} // setting a default height in case it was not provided in CMS
              sys={asset.sys}
            />
          )}
        </Column>
        <Column
          className='content-column'
          xs={24}
          smOffset={3}
          sm={18}
          lgOffset={4}
          lg={16}
        >
          <Stack
            gap={3}
            alignItems='center'
            sx={{
              textAlign: 'center',
              mt: '40px',
              '.button-group': {
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                ...buttonGroupStyles?.[cmsTheme],
              },
              ...embeddedButtonStyles?.[cmsTheme],
              '& p': {
                maxWidth: '816px',
              },
              '& p:nth-last-of-type(1):empty': {
                display: 'none',
              },
            }}
          >
            <RichTextSwitch
              sys={sys}
              content={fields?.centerColumn}
              nodes={{
                h1: {
                  component: 'h1',
                  variant: 'h1',
                  fieldId,
                },
                h2: {
                  component: 'h2',
                  variant: 'h1',
                  fieldId,
                },
                h6: {
                  component: 'h1',
                  variant: 'h6',
                  className: 'eyebrow-heading',
                  fieldId,
                },
                p: {
                  component: 'p',
                  variant: 'h5',
                  fieldId,
                },
              }}
            />
          </Stack>
        </Column>
      </Wrapper>
    </ThemeProvider>
  );
};

export default CmsLargeAssetTopCenteredHero;
